import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { CodeComboBoxModel } from '../code-combo-box/code-combo-box.model';
import { GetCodeListService } from './api-services/get-code-list.service';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 コードコンボボックスを制御するクラス
 *
 * @export CodeComboBoxComponent
 * @class CodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-code-combo-box',
  templateUrl: 'code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetCodeListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => new GetCodeListService(plessHttp, dialogService),
      deps: [PlessHttp, PlessDialogServiceSub]
    },
  ]
})

export class CodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;
  @Input() latestInfo: boolean = <boolean>false;
  @Input() codeDiv: string = null;
  @Input() conReserveNum1: string = null;
  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() isInitCodeDiv: string = null;
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() width: number | string; // どちらでも受けてTemplateに判断させる
  @Input() height: number = <number>20;
  @Input() labelField: string = <string>'CodeName';
  @Input() public initFocusItem: any = null;                                    // ロード完了後フォーカスを移動させるid
  @Input() public tabindex: number = null;
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Input() autofocus: boolean = <boolean>false;                                 // 初期フォーカスフラグ
  @Input() forcedEnabledMode: string = null;

  @Output() loadCompleted: EventEmitter<any> = new EventEmitter<any>();

  public selectedCodeDiv: string = null;
  public selectedCodeValue: string = null;
  public selectedCodeName: string = null;
  public selectedCodeValueName: string = null;
  public reserve1: string = null;
  public reserve2: string = null;
  public reserveNum1: string = null;
  public reserveNum2: string = null;
  public sortNum: string = null;
  public startOrderCode: string = null;
  public endOrderCode: string = null;
  public model: CodeComboBoxModel = new CodeComboBoxModel();
  public selectedIndex: number = <number>-1;

  /**
   * Creates an instance of codeComboBox.
   * @param {getCodeListService} GetCodeListService コードリストを取得する
   * @memberof codeComboBox
   */
  constructor(
    public elementRef: ElementRef,
    private getCodeListService: GetCodeListService,
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedCodeValue) {
      this.selectedCodeValue = text;
      this.setCodeValue(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof CodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof CodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getCodeListService.execute(this.model, this.codeDiv, this.conReserveNum1);
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof CodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'codeDiv':
          // コード区分
          this.codeDiv = changes.codeDiv.currentValue;
          if (this.model.isInitCodeDiv) {
            this.getCodeListService.execute(this.model, this.codeDiv, this.conReserveNum1);
          } else {
            this.model.isInitCodeDiv = true;
          }
          break;
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
        case 'latestInfo':
          // 初期データ自動取得フラグ
          this.model.latestInfo = changes.latestInfo.currentValue;
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof CodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.selectedCodeDiv = null;
    this.selectedCodeValue = null;
    this.selectedCodeName = null;
    this.selectedCodeValueName = null;
    this.reserve1 = null;
    this.reserve2 = null;
    this.startOrderCode = null;
    this.endOrderCode = null;
    this.model = null;
  }

  /**
   * クリア
   *
   * @memberof CodeComboBoxComponent
   */
  public clear(): void {
    this.selectedCodeDiv = null;
    this.selectedCodeValue = null;
    this.selectedCodeName = null;
    this.selectedCodeValueName = null;
    this.reserve1 = null;
    this.reserve2 = null;
    this.reserveNum1 = null;
    this.reserveNum2 = null;
    this.sortNum = null;
    this.startOrderCode = null;
    this.endOrderCode = null;
    this.selectedIndex = <number>-1;
    this.model = new CodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @param {string} codeValue コード値
   * @memberof CodeComboBoxComponent
   */
  public setCodeValue(codeValue: string): void {
    this.model.setCodeValue = codeValue;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedCodeValue();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof CodeComboBoxComponent
   */
  public updateSelectedCodeValue(): void {
    const list: any[] = this.model.codeList;

    for (let i = Number(0); i < list.length; i++) {
      if (PlessViewUtilsSub.trim(<string>list[i].CodeValue) === this.model.setCodeValue) {
        this.selectedIndex = i;
        break;
      }
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * バインドフィールド初期化
   *
   * @param {*} e
   * @returns {void}
   * @memberof CodeComboBoxComponent
   */
  public updateBindedField(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedCodeDiv = null;
      this.selectedCodeValue = null;
      this.selectedCodeName = null;
      this.selectedCodeValueName = null;
      this.reserve1 = null;
      this.reserve2 = null;
      this.reserveNum1 = null;
      this.reserveNum2 = null;
      this.sortNum = null;
      this.startOrderCode = null;
      this.endOrderCode = null;

      return;
    }

    this.selectedCodeDiv = this.model.codeList[this.selectedIndex].CodeDiv;
    this.selectedCodeValue = this.model.codeList[this.selectedIndex].CodeValue;
    this.selectedCodeName = this.model.codeList[this.selectedIndex].CodeName;
    this.selectedCodeValueName = this.model.codeList[this.selectedIndex].codeValueName;
    this.reserve1 = this.model.codeList[this.selectedIndex].Reserve1;
    this.reserve2 = this.model.codeList[this.selectedIndex].Reserve2;
    this.reserveNum1 = this.model.codeList[this.selectedIndex].ReserveNum1;
    this.reserveNum2 = this.model.codeList[this.selectedIndex].ReserveNum2;
    this.sortNum = this.model.codeList[this.selectedIndex].SortNum;
    this.startOrderCode = this.model.codeList[this.selectedIndex].StartOrderCode;
    this.endOrderCode = this.model.codeList[this.selectedIndex].EndOrderCode;

    this.loadCompleted.emit();
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @param {number} index
   * @memberof CodeComboBoxComponent
   */
  public setSelectedIndex(index: number): void {
    this.model.setSelectedIndex = index;
    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedIndex();
    }
  }

  /**
   * コンボボックスの選択Indexを切り替える
   *
   * @memberof CodeComboBoxComponent
   */
  public updateSelectedIndex(): void {
    if (this.model.setSelectedIndex >= 0) {
      this.selectedIndex = <number>this.model.setSelectedIndex;
      this.model.setSelectedIndex = -1;
      this.updateBindedField(null);
    }
  }

  /**
   * setFocusOverride
   *
   * @memberof CodeComboBoxComponent
   */
  public setFocus(): void {
    this.comboBox.nativeElement.focus();
  }

  /**
   * CodeComboBox ctp322用
   * AdminiStatusを画面内で使用するコードに変換
   * 0：全体、1：eBASE取り込み後、2：仮登録、3：本登録
   **/
  static convertToLocalAdminStatus(admitStatus: string): string {
    const map = new Map([
      ['0', '1'],
      ['1', '2'],
      ['2', '3'],
    ]);
    const localAdminStatus = map.get(admitStatus);
    return PlessViewUtilsSub.isEmpty(localAdminStatus) ? '0' : localAdminStatus;
  }

  /**
   * CodeComboBox ctp322用
   * AdminiStatusを親画面で使用するコードに変換
   * 空欄：全体、0：eBASE取り込み後、1：仮登録、2：本登録
   * 
   **/
  static convertToParentAdminStatus(admitStatus: string): string {
    const map = new Map([
      ['1', '0'],
      ['2', '1'],
      ['3', '2'],
    ]);
    const localAdminStatus = map.get(admitStatus);
    return PlessViewUtilsSub.isEmpty(localAdminStatus) ? '' : localAdminStatus;
  }
}
